import React, { useEffect, useImperativeHandle, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { Button, Col, Form, InputGroup, Modal, Offcanvas, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useField, useFormikContext } from 'formik'

import { modifyArticle } from '../articleAPI'
import { getNumberOfFullChars, getNumberOfRows, stripTags } from '../text'
import {
  changeParagraphStyleMultiline,
  getParagraphStyleName,
  handlePreamble,
  isAreaArticleType
} from '../ArticleParse'
import { useFormikReset, DirtyEffect } from '../CommonComponents'
import { getSubmitParamForArticleUpsert, TagEditor } from '..'
import { ArticleNameTitle } from '../ArticleNameTitle'
import { Article, Layoutable } from '../types'

interface PreambleValues {
  preambleContent: string | null | undefined
  preambleMarginRows: number | null | undefined
  preambleNumberOfCharactersPerRow: number | null | undefined
  preambleNumberOfColumns: number | string | null | undefined
  preambleNumberOfRows: number | null | undefined
  preambleTaggedContent: string | null | undefined
}

interface FormValues {
  preamble: PreambleValues
}

type EventHandler = {
  onSubmit: (values: FormValues) => Promise<unknown>
}

const createNewPreamble = (layoutable: Layoutable): PreambleValues => {
  const preambleContent = ''
  const preambleNumberOfColumns = 1
  const preambleTaggedContent = '<p></p>'

  const { preambleNumberOfCharactersPerRow, preambleNumberOfRows, preambleMarginRows } = handlePreamble({
    numberOfCharactersPerRow: layoutable.numberOfCharactersPerRow,
    setting: layoutable.setting,
    preamble: preambleContent,
    preambleTaggedContent,
    preambleNumberOfColumns
  })

  return {
    preambleContent,
    preambleMarginRows,
    preambleNumberOfCharactersPerRow,
    preambleNumberOfColumns,
    preambleNumberOfRows,
    preambleTaggedContent
  }
}

const createNullPreamble = (): PreambleValues => {
  return {
    preambleContent: null,
    preambleMarginRows: null,
    preambleNumberOfCharactersPerRow: null,
    preambleNumberOfColumns: null,
    preambleNumberOfRows: null,
    preambleTaggedContent: null
  }
}

interface NoPreambleFieldProps {
  formEditable: boolean
  layoutable: Layoutable
  article: Article
}

const NoPreambleField: React.FC<NoPreambleFieldProps> = ({ formEditable, layoutable, article }) => {
  const [field, meta, helper] = useField<PreambleValues>('preamble')

  const onClick = () => {
    helper.setValue(createNewPreamble(layoutable))
  }

  return (
    <DirtyEffect dirty={field.value.preambleContent !== meta.initialValue?.preambleContent}>
      <Row>
        <Col>前文なし</Col>
        {!isAreaArticleType(article.articleType) && (
          <Col>
            <Button variant='primary' size='sm' disabled={!formEditable} onClick={onClick}>
              <FontAwesomeIcon icon='plus' /> 追加
            </Button>
          </Col>
        )}
      </Row>
    </DirtyEffect>
  )
}

const InformationIndicator = () => {
  const context = useFormikContext<FormValues>()
  const preambleFullWidthCharacters = getNumberOfFullChars(context.values.preamble.preambleContent)

  return (
    <Row className='mb-2'>
      <Col>
        <div className='preambleCharacters fs-5'>前文文字数: {preambleFullWidthCharacters}</div>
      </Col>
      <Col>
        <div className='preambleNumberOfRows fs-5'>前文行数: {context.values.preamble.preambleNumberOfRows}</div>
      </Col>
    </Row>
  )
}

interface PreambleEditFieldProps {
  formEditable: boolean
  layoutable: Layoutable
}

const PreambleEditField: React.FC<PreambleEditFieldProps> = ({ formEditable, layoutable }) => {
  const context = useFormikContext<FormValues>()
  const [columnsField, columnsMeta, columnsHelper] = useField<number | string | null | undefined>({
    name: 'preamble.preambleNumberOfColumns'
  })
  const [charsPerRowField, charsPerRowMeta, charsPerRowHelper] = useField<number | string | null | undefined>({
    name: 'preamble.preambleNumberOfCharactersPerRow'
  })
  const [marginRowsField, marginRowsMeta, marginRowsHelper] = useField<number | null | undefined>({
    name: 'preamble.preambleMarginRows'
  })
  const [, , preambleNumberOfRowsHelper] = useField<number | null | undefined>({
    name: 'preamble.preambleNumberOfRows'
  })

  const { resetCount } = useFormikReset()

  const [, , contentHelper] = useField<string | null | undefined>('preamble.preambleContent')
  const [taggedField, taggedMeta, taggedHelper] = useField<string | null | undefined>('preamble.preambleTaggedContent')

  const paragraphStyleName = getParagraphStyleName(layoutable.setting, 'preamble')

  const onColumnsChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const preambleNumberOfColumns = parseInt(e.target.value)

    const { preambleNumberOfCharactersPerRow, preambleNumberOfRows, preambleMarginRows } = handlePreamble({
      numberOfCharactersPerRow: layoutable.numberOfCharactersPerRow,
      setting: layoutable.setting,
      preamble: context.values.preamble.preambleContent,
      preambleTaggedContent: context.values.preamble.preambleTaggedContent ?? undefined,
      preambleNumberOfColumns
    })

    columnsHelper.setValue(preambleNumberOfColumns)
    charsPerRowHelper.setValue(preambleNumberOfCharactersPerRow)
    marginRowsHelper.setValue(preambleMarginRows)
    context.setFieldValue('preamble.preambleNumberOfRows', preambleNumberOfRows)
  }

  const onCharsPerRowChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const preambleNumberOfCharactersPerRow = parseInt(e.target.value)
    const preamble = context.values.preamble.preambleContent
    const preambleNumberOfRows = !preamble
      ? null
      : (getNumberOfRows(preambleNumberOfCharactersPerRow, preamble, false) ?? null)
    preambleNumberOfRowsHelper.setValue(preambleNumberOfRows)
    charsPerRowHelper.setValue(preambleNumberOfCharactersPerRow)
  }

  const onTagEditorChange = (taggedContent: string, _old: string | undefined) => {
    const content = stripTags(taggedContent)
    contentHelper.setValue(content ?? null)
    taggedHelper.setValue(taggedContent)
    const preambleNumberOfRows = !content
      ? null
      : (getNumberOfRows(Number(charsPerRowField.value), content, false) ?? null)
    preambleNumberOfRowsHelper.setValue(preambleNumberOfRows)
  }

  return (
    <>
      <Row className='mb-2'>
        <Col>
          <DirtyEffect dirty={columnsField.value !== columnsMeta.initialValue}>
            <InputGroup size='sm' hasValidation>
              <InputGroup.Text>縦</InputGroup.Text>
              <Form.Control
                {...columnsField}
                value={columnsField.value ?? ''}
                type='number'
                min={1}
                onChange={onColumnsChange}
                disabled={!formEditable}
                isInvalid={!!(columnsMeta.touched && columnsMeta.error)}
              />
              <InputGroup.Text>段</InputGroup.Text>
              <Form.Control.Feedback type='invalid'>{columnsMeta.error}</Form.Control.Feedback>
            </InputGroup>
          </DirtyEffect>
        </Col>
        <Col>
          <DirtyEffect dirty={charsPerRowField.value !== charsPerRowMeta.initialValue}>
            <InputGroup size='sm' hasValidation>
              <Form.Control
                {...charsPerRowField}
                value={charsPerRowField.value ?? ''}
                type='number'
                min={1}
                onChange={onCharsPerRowChange}
                disabled={!formEditable}
                isInvalid={!!(charsPerRowMeta.touched && charsPerRowMeta.error)}
              />
              <InputGroup.Text>字組</InputGroup.Text>
              <Form.Control.Feedback type='invalid'>{charsPerRowMeta.error}</Form.Control.Feedback>
            </InputGroup>
          </DirtyEffect>
        </Col>
        <Col>
          <DirtyEffect dirty={marginRowsField.value !== marginRowsMeta.initialValue}>
            <InputGroup size='sm' hasValidation>
              <Form.Control
                {...marginRowsField}
                value={marginRowsField.value ?? ''}
                type='number'
                min={1}
                disabled={!formEditable}
                isInvalid={!!(marginRowsMeta.touched && marginRowsMeta.error)}
              />
              <InputGroup.Text>行取り</InputGroup.Text>
              <Form.Control.Feedback type='invalid'>{marginRowsMeta.error}</Form.Control.Feedback>
            </InputGroup>
          </DirtyEffect>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <DirtyEffect dirty={taggedField.value !== taggedMeta.initialValue}>
            <TagEditor
              key={resetCount}
              initialTaggedContent={taggedField.value ?? undefined}
              styleTagType='sentence'
              paragraphStyleName={paragraphStyleName}
              setting={layoutable.setting}
              onSubmit={(_e) => undefined}
              disabled={!formEditable}
              editable={formEditable}
              onChange={onTagEditorChange}
              allowLineBreak
              allowMultiParagraph
              appearance='withoutSubmit'
            />
          </DirtyEffect>
        </Col>
      </Row>
    </>
  )
}

interface RemovePreambleFieldProps {
  formEditable: boolean
}

const RemovePreambleField: React.FC<RemovePreambleFieldProps> = ({ formEditable }) => {
  const [, , helper] = useField<PreambleValues>('preamble')
  const [modalShowing, setModalShowing] = useState(false)

  const onClick = () => {
    setModalShowing(true)
  }

  const onCancelClick = () => {
    setModalShowing(false)
  }

  const onRemoveClick = () => {
    helper.setValue(createNullPreamble())
    setModalShowing(false)
  }

  return (
    <>
      <Button variant='danger' size='sm' disabled={!formEditable} onClick={onClick}>
        <FontAwesomeIcon icon='trash' /> 前文削除
      </Button>
      <Modal show={modalShowing}>
        <Modal.Header>
          <Modal.Title>前文削除</Modal.Title>
        </Modal.Header>
        <Modal.Body>前文を削除します。保存するまでは元に戻せます。</Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onCancelClick}>
            キャンセル
          </Button>
          <Button variant='danger' onClick={onRemoveClick}>
            前文削除
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

interface PreambleFieldProps {
  formEditable: boolean
  layoutable: Layoutable
  article: Article
}

const PreambleField: React.FC<PreambleFieldProps> = ({ formEditable, layoutable }) => {
  return (
    <>
      <InformationIndicator />
      <PreambleEditField formEditable={formEditable} layoutable={layoutable} />
      <RemovePreambleField formEditable={formEditable} />
    </>
  )
}

interface ArticlePreambleDetailBodyProps {
  formEditable: boolean
  layoutable: Layoutable
  article: Article
}

const ArticlePreambleDetailBody: React.FC<ArticlePreambleDetailBodyProps> = (props) => {
  const context = useFormikContext<FormValues>()

  const field =
    context.values.preamble.preambleContent === null ? <NoPreambleField {...props} /> : <PreambleField {...props} />
  return <>{field}</>
}

interface Props {
  article: Article
  layoutable: Layoutable
  layoutableId: string | number
  layoutableType: string
  enumValues: unknown
  isSelectedLayoutableWritable: boolean
  eventHandlerRef: React.RefObject<EventHandler | null>
  handleChangeArticleSubmit?: unknown
}

export const ArticlePreambleDetail: React.FC<Props> = ({
  article,
  layoutable,
  layoutableId,
  layoutableType,
  isSelectedLayoutableWritable,
  eventHandlerRef
}) => {
  const formEditable = isSelectedLayoutableWritable && !article.layoutLocked

  const context = useFormikContext<Partial<FormValues>>()
  const formikReset = useFormikReset()
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>()

  useEffect(() => {
    const initialValues: FormValues = {
      preamble: {
        preambleContent: article.preambleContent,
        preambleMarginRows: article.preambleMarginRows,
        preambleNumberOfCharactersPerRow: article.preambleNumberOfCharactersPerRow,
        preambleNumberOfColumns: article.preambleNumberOfColumns,
        preambleNumberOfRows: article.preambleNumberOfRows,
        preambleTaggedContent: article.preambleTaggedContent
      }
    }

    context.resetForm({ values: initialValues })

    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [])

  useImperativeHandle(eventHandlerRef, () => ({
    onSubmit: async (_values: FormValues) => {
      const paragraphStyleName = getParagraphStyleName(layoutable.setting, 'preamble') ?? ''
      const preamble = context.values.preamble && {
        ...context.values.preamble,
        preambleContent: stripTags(context.values.preamble.preambleContent),
        preambleTaggedContent: changeParagraphStyleMultiline(
          context.values.preamble.preambleTaggedContent,
          paragraphStyleName
        )
      }
      const newArticle = { ...article, ...preamble }
      const props = {
        layoutableType,
        layoutableId,
        layoutableLockVersion: layoutable?.lockVersion,
        article: newArticle,
        loadedBinaryFile: undefined,
        deleteFile: undefined,
        auxiliaryLine: null,
        successAlertBody: undefined
      }
      const submitParam = getSubmitParamForArticleUpsert(props) as Parameters<typeof modifyArticle>[0]

      return dispatch(modifyArticle(submitParam)).then(() => {
        formikReset.resetForm({ values: context.values })
      })
    }
  }))

  if ((context.values as Partial<FormValues> | undefined)?.preamble === undefined) {
    return undefined
  }

  return (
    <>
      <Offcanvas.Header>
        <Offcanvas.Title>
          前文
          <ArticleNameTitle article={article} />
        </Offcanvas.Title>
      </Offcanvas.Header>
      <div
        className='flex-grow-1 overflow-auto m-1'
        style={{
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: 'var(--bs-border-color)'
        }}
      >
        <Offcanvas.Body className='article-preamble-detail-offcanvas-body'>
          <ArticlePreambleDetailBody article={article} layoutable={layoutable} formEditable={!!formEditable} />
        </Offcanvas.Body>
      </div>
    </>
  )
}
